import React from "react";

// @material-ui/core components
import { makeStyles } from "@material-ui/styles";
import Hidden from "@material-ui/core/Hidden";
import Paper from "@material-ui/core/Paper";

// core components
import GridContainer from "components/GridContainer.jsx";
import GridItem from "components/GridItem.jsx";

import timelineVideo from "assets/video/optaplan-timeline.mp4";
import timelinePoster from "assets/video/optaplan-timeline-poster.png";
import tnaAdjust from "assets/video/tna-adjust.mp4";
import infoAtGlance from "assets/video/information-at-glance.png";
import collaboration from "assets/video/collaboration-among-departments.mp4";
import alertNotfication from "assets/video/alert-and-notification.png";
import apiIntegration from "assets/video/integration.png";
import customization from "assets/video/customization.mp4";

import { container, title, blackColor } from "assets/jss/material-kit-pro-react.jsx";

const useStyles = makeStyles(theme => ({
  container,
  features: {
    padding: "70px 0",
  },
  imageContainer: {
    width: "90%",
    boxShadow: "6px 6px 10px 0px rgba(0, 0, 0, 0.2), -6px 6px 10px 0px rgba(0, 0, 0, 0.2)",
    marginTop: "2em",
  },
  contentContainer: {
    width: "90%",
  },
  image: {
    width: "100%",
    height: "auto",
  },
  title: {
    ...title,
    lineHeight: "42px",
  },
  text: {
    color: blackColor,
    overflow: "hidden",
    marginTop: "0px",
    "& p": {
      color: blackColor,
      fontSize: "1.1em",
      letterSpacing: 0.5,
      lineHeight: "26px",
    },
  },
  video: {
    width: "100%",
    height: "auto",
  },
  justifyCenter: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const Feature = ({ vRef, srcVideo, poster, srcImg, imgAltText, title, description, align = "left", content }) => {
  const classes = useStyles();

  const contentMedia = (
    <Paper elevation={0} className={classes.imageContainer}>
      {srcImg && <img className={classes.image} src={srcImg} alt={imgAltText} />}
      {srcVideo && <video className={classes.video} ref={vRef} src={srcVideo} poster={poster} loop muted />}
    </Paper>
  );
  return (
    <div className={classes.features}>
      <GridContainer alignItems="center" justify="space-between">
        {align === "left" && (
          <Hidden xsDown>
            <GridItem xs={12} sm={6} md={6} className={classes.justifyCenter}>
              {contentMedia}
            </GridItem>
          </Hidden>
        )}
        <GridItem xs={12} sm={5} md={5} className={classes.justifyCenter}>
          {title && description && (
            <div className={classes.contentContainer}>
              <h2 className={classes.title}>{title}</h2>
              <div className={classes.text}>
                <p>{description}</p>
              </div>
            </div>
          )}
          {content}
        </GridItem>
        {align === "left" && (
          <Hidden smUp>
            <GridItem xs={12} sm={6} md={6} className={classes.justifyCenter}>
              {contentMedia}
            </GridItem>
          </Hidden>
        )}
        {align === "right" && (
          <GridItem xs={12} sm={6} md={6} className={classes.justifyCenter}>
            {contentMedia}
          </GridItem>
        )}
      </GridContainer>
    </div>
  );
};

const SectionFeatures = props => {
  const classes = useStyles();

  const height = window.innerHeight || document.documentElement.clientHeight;

  const features = [
    {
      title: "Custom Timeline for each Buyer",
      description:
        "Create a custom template for each buyer and product category. Each template can have different activities and sub-activities.",
      srcVideo: timelineVideo,
      poster: timelinePoster,
      vRef: React.useRef(),
      align: "left",
    },
    {
      title: "TNA that adjusts as per lead time",
      description:
        "Merchandisers can create a Time and Action Calendar in seconds using the Timeline Template. OptaPlan automatically plans the due date for each activity based on the actual lead time.",
      srcVideo: tnaAdjust,
      // poster: timelinePoster,
      vRef: React.useRef(),
      align: "right",
    },
    {
      title: "Information at a glance",
      description:
        "OptaPlan Grid view allows you to track every activity of all orders on a single screen. Quickly determine tasks that are critical through intuitive color-coding.",
      srcImg: infoAtGlance,
      imgAltText: "Information at a glance",
      align: "left",
    },
    {
      title: "Collaboration among departments",
      description:
        "Bring together all the departments on one platform to ensure proper accountability. Each team can track and manage their activities transparently.",
      srcVideo: collaboration,
      vRef: React.useRef(),
      align: "right",
    },
    {
      title: "Critical alerts and notifications",
      description:
        "OptaPlan sends alerts for overdue activities. It also sends notifications when a task is updated or completed by other departments.",
      srcImg: alertNotfication,
      imgAltText: "Critical alerts and notifications",
      align: "left",
    },
    {
      title: "Flexible & Customizable",
      description:
        "OptaPlan is fully customizable and flexible to meet your dynamic needs. Create your own activity master, departments, timelines and much more.",
      srcVideo: customization,
      vRef: React.useRef(),
      // imgAltText: "Flexible & Customizable",
      align: "right",
    },
    {
      title: "Integrate with ERP and other solutions",
      description:
        "OptaPlan flexible API enables full integration with your organization's existing software tools for a seamless experience.",
      srcImg: apiIntegration,
      imgAltText: "Integrate with ERP and other solutions",
      align: "left",
    },
  ];

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
  }, []);
  /* eslint-disable react-hooks/exhaustive-deps */

  const handleScroll = () => {
    features.forEach(f => {
      if (f.vRef?.current) {
        const rect = f.vRef.current.getBoundingClientRect();
        const vh = rect.bottom - rect.top; // video frame height
        // Start Playing video only when frame is fully visible
        if (rect.top > 0 && rect.top < height - vh) {
          f.vRef.current.play();
        } else {
          f.vRef.current.pause();
        }
      }
    });
  };

  return (
    <div className="cd-section" {...props}>
      <div className={classes.container}>
        {features.map((feature, idx) => (
          <Feature key={idx} {...feature} />
        ))}
      </div>
    </div>
  );
};

export default SectionFeatures;
